/**
 * NMS Store Launch · Journey timeline
 * Draws the store line as SVG once (rebuilt only when switching between the
 * mobile and desktop geometry) and then only updates attributes per step.
 * Playback is a rAF loop that pauses when the card leaves the viewport.
 */
(() => {
  const SVG_NS = 'http://www.w3.org/2000/svg';

  class NMSSlTimeline extends window.NMSBaseClass {
    constructor(element, service) {
      super();
      this.element = element;
      this.sl = service;
      this.labels = element.dataset;
      this.controller = new AbortController();
      this.index = 0;
      this.playing = false;
      this.done = false;
      this.mq = window.matchMedia('(max-width: 759px)');
    }

    init() {
      super.init();
      const { signal } = this.controller;
      this.stores = this.sl.stores;
      this.N = this.stores.length;
      if (!this.N || !this.refs.svg) return;
      this.refs.total.textContent = `/ ${this.sl.launch.number}`;
      this.refs.range.max = String(this.N);
      this.#build();
      this.#render(0);
      this.mq.addEventListener('change', () => {
        this.#build();
        this.#render(this.index);
      }, { signal });

      this.refs.toggle.addEventListener('click', () => (this.playing ? this.#pause(false) : this.#play()), { signal });
      this.refs.range.addEventListener('input', event => this.#go((+event.target.value || 1) - 1), { signal });
      this.refs.cta.addEventListener('click', () => {
        this.#pause(false);
        this.sl.showStore(this.index, 'timeline');
      }, { signal });
      this.refs.svg.addEventListener('click', event => {
        const dot = event.target.closest('circle[data-i]');
        if (dot) this.#go(+dot.dataset.i);
      }, { signal });

      this.io = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        if (entry.intersectionRatio >= .45 && !this.started && this.labels.autoplay === 'true') {
          this.started = true;
          this.sl.afterIntro('done', () => this.#play(), signal);
        }
        if (!entry.isIntersecting && this.playing) this.#pause(true);
        else if (entry.isIntersecting && this.autoPaused && !this.done) this.#play();
      }, { threshold: [0, .45] });
      this.io.observe(this.element);
    }

    #geometry() {
      const mobile = this.mq.matches;
      // Figma: desktop line box 1416×322, mobile 351×111; 24px vertical inset.
      const VW = mobile ? 351 : 1416;
      const VH = mobile ? 111 : 322;
      const padX = mobile ? 12 : 28;
      const pts = this.stores.map((_, i) => {
        const u = this.N > 1 ? i / (this.N - 1) : 0;
        return [padX + u * (VW - 2 * padX), VH / 2 + (VH / 2 - 24) * Math.sin(u * Math.PI * 3 - Math.PI * .35)];
      });
      // Dot radii: small stop, milestone (first, every 10th, last), current stop.
      const r = mobile ? { small: 1.5, big: 5, on: 8, line: 1.25 } : { small: 6, big: 11.5, on: 17, line: 1.5 };
      return { mobile, VW, VH, pts, r };
    }

    #build() {
      const g = (this.geo = this.#geometry());
      const svg = this.refs.svg;
      svg.setAttribute('viewBox', `0 0 ${g.VW} ${g.VH}`);
      let d = '';
      g.pts.forEach((p, i) => {
        if (!i) {
          d = `M${p[0].toFixed(1)} ${p[1].toFixed(1)}`;
          return;
        }
        const q = g.pts[i - 1];
        const mx = ((q[0] + p[0]) / 2).toFixed(1);
        d += ` C${mx} ${q[1].toFixed(1)} ${mx} ${p[1].toFixed(1)} ${p[0].toFixed(1)} ${p[1].toFixed(1)}`;
      });
      const last = this.N - 1;
      const dots = g.pts.map((p, i) => {
        const big = i === 0 || i === last || (i + 1) % 10 === 0;
        return `<circle data-i="${i}" cx="${p[0].toFixed(1)}" cy="${p[1].toFixed(1)}" class="${big ? 'is-big' : ''}${i === last ? ' is-last' : ''}"></circle>`;
      }).join('');
      const fw = g.mobile ? 38 : 60;
      const fh = Math.round((fw * 190) / 440);
      svg.innerHTML = `
        <path class="nms-sl-timeline__base" d="${d}" fill="none" stroke-width="${g.r.line}" stroke-linecap="round"></path>
        <path class="nms-sl-timeline__progress" d="${d}" pathLength="1" fill="none" stroke-width="${g.r.line}" stroke-linecap="round" stroke-dasharray="1 1" stroke-dashoffset="1"></path>
        ${dots}
        <image href="${this.sl.config.assets.footCream}" x="${-fw / 2}" y="${-fh / 2 - g.r.on - fh / 2 - 4}" width="${fw}" height="${fh}" style="pointer-events:none"></image>`;
      this.progressPath = svg.querySelector('.nms-sl-timeline__progress');
      this.dots = [...svg.querySelectorAll('circle')];
      this.foot = svg.querySelector('image');
    }

    #render(i) {
      const g = this.geo;
      const s = this.stores[i] || {};
      const last = this.N - 1;
      const L = this.sl.launch;
      const { pad2, fill } = this.sl.helpers;
      const ratio = this.N > 1 ? i / last : 1;

      this.progressPath.setAttribute('stroke-dashoffset', (1 - ratio).toFixed(4));
      this.dots.forEach((dot, k) => {
        const big = dot.classList.contains('is-big');
        const on = k === i;
        dot.classList.toggle('is-on', on);
        dot.classList.toggle('is-done', k <= i);
        dot.setAttribute('r', on ? g.r.on : big ? g.r.big : g.r.small);
      });
      const a = Math.max(0, Math.min(last - 1, i));
      const p = g.pts[i];
      const angle = g.pts[a + 1] ? (Math.atan2(g.pts[a + 1][1] - g.pts[a][1], g.pts[a + 1][0] - g.pts[a][0]) * 180) / Math.PI : 0;
      this.foot.setAttribute('transform', `translate(${p[0].toFixed(1)} ${p[1].toFixed(1)}) rotate(${angle.toFixed(1)})`);
      this.refs.range.style.setProperty('--p', `${(ratio * 100).toFixed(2)}%`);

      this.refs.num.textContent = pad2(i + 1);
      this.refs.state.textContent = i === 0
        ? this.sl.config.chapters?.first || s.chapter
        : i === last
          ? (this.sl.isLive() ? s.chapter : fill(this.labels.labelLast, { date: L.dateShort }))
          : fill(this.sl.t.store, { num: pad2(i + 1) });
      this.refs.area.textContent = s.title || '';
      this.refs.city.textContent = s.city ? [s.city, s.state].filter(Boolean).join(', ') : s.chapter || '';
      this.refs.range.value = String(i + 1);
      this.refs.ctaLabel.textContent = fill(this.labels.cta, { number: pad2(i + 1) });
      this.#renderControls();
    }

    #renderControls() {
      const L = this.labels;
      const state = this.playing ? 'pause' : this.done ? 'replay' : 'play';
      if (state === this.controlState) return;
      this.controlState = state;
      this.refs.toggleIcon.innerHTML = this.sl.helpers.icon(state, 24);
      this.refs.toggle.setAttribute('aria-label', this.playing ? L.labelPause : this.done ? L.labelReplay : L.labelPlay);
    }

    #play() {
      const last = this.N - 1;
      if (this.sl.reducedMotion) {
        this.done = true;
        this.#render(last);
        return;
      }
      const from = this.done ? 0 : this.index;
      this.done = false;
      this.playing = true;
      this.autoPaused = false;
      const duration = (+this.labels.duration || 11000) * (1 - from / this.N);
      const t0 = performance.now();
      cancelAnimationFrame(this.raf);
      const step = now => {
        const t = Math.min(1, (now - t0) / Math.max(1, duration));
        const i = Math.round(from + (last - from) * t);
        if (i !== this.index) {
          this.index = i;
          if ((i + 1) % 10 === 0) this.#buzz();
          this.#render(i);
        }
        if (t < 1) {
          this.raf = requestAnimationFrame(step);
        } else {
          this.playing = false;
          this.done = true;
          this.#render(last);
        }
      };
      this.#renderControls();
      this.raf = requestAnimationFrame(step);
    }

    #pause(auto) {
      cancelAnimationFrame(this.raf);
      this.playing = false;
      this.autoPaused = auto;
      this.#renderControls();
    }

    #go(i) {
      const j = Math.max(0, Math.min(this.N - 1, i));
      this.#pause(false);
      this.index = j;
      this.done = j === this.N - 1;
      this.#render(j);
      this.sl.select(j, 'timeline');
    }

    #buzz() {
      try {
        if (navigator.userActivation?.hasBeenActive && navigator.vibrate) navigator.vibrate(12);
      } catch (error) {
        /* unsupported */
      }
    }

    destroy() {
      this.controller.abort();
      cancelAnimationFrame(this.raf);
      this.io?.disconnect();
      super.destroy();
    }
  }

  const register = () => window.neemans.storeLaunch.register('[data-nms-sl-timeline]', NMSSlTimeline);
  if (window.neemans?.storeLaunch) register();
  else document.addEventListener('nms-sl:ready', register, { once: true });
})();
